// IndexedDB cache for downloaded mesh GLB/FBX blobs, keyed by mesh id. A mesh's
// geometry is immutable for its id (a regeneration / upscale produces a new id),
// so re-opening a previously-viewed mesh can serve the bytes straight from this
// cache and skip the Storage signed-URL + download entirely. Best-effort: every
// path degrades silently to a normal download when IndexedDB is unavailable
// (private mode, SSR, quota) so it can never block or break a mesh load.

const DB_NAME = 'tk-mesh-cache';
const STORE = 'blobs';
const VERSION = 1;

let dbPromise: Promise<IDBDatabase> | null = null;

function getDb(): Promise<IDBDatabase> {
  if (typeof indexedDB === 'undefined') {
    return Promise.reject(new Error('indexedDB unavailable'));
  }
  if (!dbPromise) {
    dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
      const req = indexedDB.open(DB_NAME, VERSION);
      req.onupgradeneeded = () => {
        if (!req.result.objectStoreNames.contains(STORE)) {
          req.result.createObjectStore(STORE);
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
    // If the open fails, clear the memoized promise so a later call can retry.
    dbPromise.catch(() => {
      dbPromise = null;
    });
  }
  return dbPromise;
}

/** Returns the cached blob for a mesh id, or null on miss / any error. */
export async function getCachedMeshBlob(id: string): Promise<Blob | null> {
  try {
    const db = await getDb();
    return await new Promise<Blob | null>((resolve) => {
      const req = db.transaction(STORE, 'readonly').objectStore(STORE).get(id);
      req.onsuccess = () =>
        resolve(req.result instanceof Blob ? req.result : null);
      req.onerror = () => resolve(null);
    });
  } catch {
    return null;
  }
}

/** Stores a mesh blob under its id. Fire-and-forget; never throws. */
export async function putCachedMeshBlob(id: string, blob: Blob): Promise<void> {
  try {
    const db = await getDb();
    await new Promise<void>((resolve) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).put(blob, id);
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
      tx.onabort = () => resolve();
    });
  } catch {
    /* best-effort cache — ignore */
  }
}
